<template>
    <div ref="loader">
        <b-container class="container-lg">
            <b-row v-if="ready" class="pb-2">
                <b-col class="col-9 text-start min-width-0">
                    <h5 class="text-truncate">
                        <i class="fa-solid fa-chevron-left" @click="goBack"></i>
                        {{ slaview.sla?.name }}
                    </h5>
                </b-col>
                <b-col class="col-3 text-end">
                    <button class="btn btn-light btn-sm" type="button" @click="refreshData">
                        <i :class="{'fa-spin': loading}" class="fa-solid fa-arrows-rotate"></i>
                    </button>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b>{{ $t('Container') }}: </b>
                    <small>
                        <span v-for="container in slaview.sla?.main_container">
                            /{{ container.name }}
                        </span>
                    </small>
                </b-col>
            </b-row>
            <b-list-group-item
                class="list-group-item-details p-2 border-none border-top">
                <div class="d-flex w-100 justify-content-between mt-2">
                    <div>
                        <small><b>{{ $t('Evaluation interval') }}:</b></small>
                    </div>
                    <div>
                        <small>{{ slaview.sla?.evaluation_interval }}</small>
                    </div>
                </div>
                <div class="d-flex w-100 justify-content-between mt-2">
                    <div>
                        <small><b>{{ $t('Time period') }}:</b></small>
                    </div>
                    <div>
                        <small>{{ slaview.sla?.timeperiod.name }}</small>
                    </div>
                </div>
                <div class="d-flex w-100 justify-content-between mt-2">
                    <div>
                        <small><b>{{ $t('Thresholds') }}:</b></small>
                    </div>
                    <div>
                        <span v-if="slaview.sla?.warning_threshold"
                              class="icon badge text-warning border border-warning mx-1" slaview.>
                            <i class="fa-solid fa-less-than"></i> {{ slaview.sla?.warning_threshold }} %
                        </span>
                        <span v-if="slaview.sla?.minimal_availability"
                              class="icon badge text-danger border border-danger mx-1">
                            <i class="fa-solid fa-less-than"></i> {{ slaview.sla?.minimal_availability }} %
                        </span>
                    </div>
                </div>
                <div class="d-flex w-100 justify-content-between mt-2">
                    <div>
                        <small><b>{{ $t('Consider downtimes') }}:</b></small>
                    </div>
                    <div>
                        <small>
                            <span v-if="slaview.sla?.consider_downtimes" class="badge bg-up">
                                {{ $t('yes') }}
                            </span>
                            <span v-if="!slaview.sla?.consider_downtimes" class="badge bg-down">
                                {{ $t('no') }}
                            </span>
                        </small>
                    </div>
                </div>
                <div class="d-flex w-100 justify-content-between mt-2">
                    <div>
                        <small><b>{{ $t('Reflection state') }}:</b></small>
                    </div>
                    <div>
                        <small>
                            <span v-if="!slaview.sla?.hard_state_only">
                                {{ $t('soft and hard state') }}
                            </span>
                            <span v-if="slaview.sla?.hard_state_only">
                                {{ $t('hard state only') }}
                            </span>
                        </small>
                    </div>
                </div>

                <b-row class="mt-4">
                    <div class="col">
                        <div class="p-2 bg-danger rounded overflow-hidden position-relative text-white mb-4">
                            <h6 class="display-4 d-block l-h-n m-0 fw-500">
                                <span v-if="slaview.sla?.hostsOverview?.failed.count > 0">
                                    <router-link
                                        :to="{name: 'hostsIndex', query: {id: slaview.sla.hostsOverview?.failed?.ids}}"
                                        class="link-underline link-underline-opacity-0 text-white">
                                        {{ slaview.sla?.hostsOverview?.failed.count }}
                                    </router-link>

                                </span>
                                <span v-else>
                                    {{ slaview.sla?.hostsOverview?.failed.count }}
                                </span>
                                <small class="d-block m-0 l-h-n">
                                    {{ $t('Failed') }}
                                </small>
                            </h6>
                        </div>
                    </div>
                    <div class="col">
                        <div class="p-2 bg-warning rounded overflow-hidden position-relative text-white mb-4">
                            <h6 class="display-4 d-block l-h-n m-0 fw-500">
                                <span v-if="slaview.sla?.hostsOverview?.warning.count > 0">
                                    <router-link
                                        :to="{name: 'hostsIndex', query: {id: slaview.sla.hostsOverview?.warning?.ids}}"
                                        class="link-underline link-underline-opacity-0 text-white">
                                        {{ slaview.sla?.hostsOverview?.warning.count }}
                                    </router-link>

                                </span>
                                <span v-else>
                                    {{ slaview.sla?.hostsOverview?.warning.count }}
                                </span>
                                <small class="d-block m-0 l-h-n">
                                    {{ $t('Warning') }}
                                </small>
                            </h6>
                        </div>
                    </div>
                    <div class="col">
                        <div class="p-2 bg-success rounded overflow-hidden position-relative text-white mb-4">
                            <h6 class="display-4 d-block l-h-n m-0 fw-500">
                                <span v-if="slaview.sla?.hostsOverview?.passed.count > 0">
                                    <router-link
                                        :to="{name: 'hostsIndex', query: {id: slaview.sla.hostsOverview?.passed?.ids}}"
                                        class="link-underline link-underline-opacity-0 text-white">
                                        {{ slaview.sla?.hostsOverview?.passed.count }}
                                    </router-link>

                                </span>
                                <span v-else>
                                    {{ slaview.sla?.hostsOverview?.passed.count }}
                                </span>
                                <small class="d-block m-0 l-h-n">
                                    {{ $t('Passed') }}
                                </small>
                            </h6>
                        </div>
                    </div>
                    <div class="col">
                        <div class="p-2 bg-primary-custom rounded overflow-hidden position-relative text-white mb-4">
                            <h6 class="display-4 d-block l-h-n m-0 fw-500">
                                <span v-if="slaview.sla?.hostsOverview?.not_calculated.count > 0">
                                    <router-link
                                        :to="{name: 'hostsIndex', query: {id: slaview.sla.hostsOverview?.not_calculated?.ids}}"
                                        class="link-underline link-underline-opacity-0 text-white">
                                        {{ slaview.sla?.hostsOverview?.not_calculated.count }}
                                    </router-link>

                                </span>
                                <span v-else>
                                    {{ slaview.sla?.hostsOverview?.not_calculated.count }}
                                </span>
                                <small class="d-block m-0 l-h-n">
                                    {{ $t('Not calc.') }}
                                </small>
                            </h6>
                        </div>
                    </div>

                </b-row>
            </b-list-group-item>
            <div class="w-100">
                <sla-pie-echarts :availability="slaview.availability"></sla-pie-echarts>
            </div>
            <b-list-group-item v-if="slaview.sla?.hostsOverview?.top10.length > 0 "
                               class="list-group-item-details p-0 px-2 border-none border-top">
                <b-row>
                    <b-col>
                        <b> {{ 'Top10 achieved' }} </b>
                    </b-col>
                </b-row>
                <div class="d-flex w-100 justify-content-between mt-2">
                    <div>
                        <small><b>{{ $t('Host') }}:</b></small>
                    </div>
                    <div>
                        <small><b>{{ $t('Achieved') }}(%):</b></small>
                    </div>
                </div>
                <div v-for="item in slaview.sla?.hostsOverview?.top10"
                     class="d-flex w-100 justify-content-between mt-2">
                    <div>
                        <router-link
                            :to="{name: 'hostsDetails', params: {id: item.id}}"
                            class="link-underline link-underline-opacity-0">
                            {{ item.name }}
                        </router-link>
                    </div>
                    <div>
                        <small
                            :class="{'text-success': item.state === 'success', 'text-warning': item.state === 'warning', 'text-danger': item.state === 'danger'}"><b>{{
                                item.determined_availability
                            }}</b></small>
                    </div>
                </div>
            </b-list-group-item>
        </b-container>
    </div>
</template>

<script>
import { BListGroup, BListGroupItem } from 'bootstrap-vue-next';
import SlaPieEcharts from './SlaPieEcharts.vue'

export default {
    name: "SlasDetails",
    components: {BListGroup, BListGroupItem, SlaPieEcharts},
    data() {
        return {
            ready: false,
            loading: false,
            slaview: {}
        }
    },

    mounted() {
        this.getData();
    },

    methods: {
        getData: function() {
            const id = this.$route.params.id;
            let loader = this.$loading.show();
            this.loading = true;
            axios.post('/slas/details.json', null, {
                params: {
                    id: id
                }
            })
                .then(response => {
                    this.slaview = response.data.slaview ?? {};
                    //console.log(this.slaview)
                    this.ready = true;
                })
                .catch(error => {
                    console.log(error)
                })
                .finally(() => {
                    loader.hide();
                    this.loading = false;
                });
        },
        goBack() {
            this.$router.go(-1);
        },
        refreshData() {
            this.getData();
        }
    }
}
</script>


<style scoped>
.display-4 {
    font-size: 2.5rem;
    font-weight: 300;
    line-height: 1.57;
}

/* line-heights */
.l-h-n {
    line-height: normal;
}

.fw-500 {
    font-weight: 500 !important;
}

h5 small,
h6 small,
.h5 small,
.h6 small {
    font-size: 0.8125rem;
}

h6 {
    font-size: 1.2rem !important;
}

.font-5-rem {
    font-size: 5rem;
}

.pos-bottom-negative-23 {
    bottom: -23px;
}

.inside-list {
    list-style-position: inside;
}

.state-button-small {
    padding: 0 2px;
    font-size: 12px;
}
</style>
